'use client';

import { useNavigationLoading } from '@lib/hooks/use-navigation-loading';
import { convertToLocale } from '@lib/util/money';
import { CartItemCard } from '@modules/cart/components/cart-item-card';
import { Button } from '@modules/common/components/button';
import { EmptyBasket } from '@modules/common/icons';
import type { CartDropdownVM } from 'types/view-models';

// Wired via @chemist4u/newt-components' `THeaderBasket.renderPreview` slot —
// GlobalNavigationClient.tsx passes this as the basket icon's hover/focus
// preview panel body. The design system owns open/close, anchor positioning,
// and the transition; this component only owns the cart content itself.
const BasketPreview = ({
  cart,
  onRequestClose,
}: {
  cart?: CartDropdownVM | null;
  onRequestClose?: () => void;
}) => {
  const { navigate, isPending } = useNavigationLoading();

  const subtotal = cart?.item_subtotal ?? 0;

  const goToBasket = () => {
    onRequestClose?.();
    navigate('/basket');
  };

  const exploreProducts = () => {
    onRequestClose?.();
    navigate('/search');
  };

  return (
    <div
      className="bg-white border border-border-semantic-subtle shadow-lg w-[420px] max-w-[calc(100vw-2rem)] text-ui-fg-base rounded-lg"
      data-testid="nav-cart-dropdown"
    >
      {cart?.items?.length ? (
        <>
          <div className="overflow-y-scroll max-h-[402px] px-4 pt-4 py-px flex flex-col gap-y-6 no-scrollbar">
            {[...cart.items]
              .sort((a, b) => {
                const aDate = a.created_at ?? '';
                const bDate = b.created_at ?? '';
                if (aDate === bDate) {
                  return 0;
                }
                return aDate > bDate ? -1 : 1;
              })
              .map((item) => (
                <div key={item.id} data-testid="cart-item">
                  <CartItemCard
                    item={item}
                    currencyCode={cart.currency_code}
                    compact
                    onNavigate={onRequestClose}
                  />
                </div>
              ))}
          </div>
          <div className="p-4 flex flex-col gap-y-4 nw-typography-paragraph-xs">
            <div className="flex items-center justify-between">
              <span className="text-ui-fg-base font-semibold">Subtotal</span>
              <span
                className="nw-typography-subheading font-semibold"
                data-testid="cart-subtotal"
                data-value={subtotal}
                translate="no"
              >
                {convertToLocale({
                  amount: subtotal,
                  currency_code: cart.currency_code,
                })}
              </span>
            </div>
            <Button
              variant="primary"
              size="lg"
              className="w-full"
              isLoading={isPending}
              data-testid="go-to-cart-button"
              onClick={goToBasket}
            >
              Go to Basket
            </Button>
          </div>
        </>
      ) : (
        <div>
          <div className="flex py-16 flex-col gap-y-4 items-center justify-center">
            <EmptyBasket
              size="32"
              className="text-fg-interactive-strong"
              aria-hidden="true"
            />
            <span>Your basket is empty</span>
            <div>
              <Button
                variant="primary"
                isLoading={isPending}
                onClick={exploreProducts}
              >
                Explore Products
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BasketPreview;
